import { getUserToken } from "src/utils/jwtToken";
import { useActingAsOverrideHeader } from "src/auth";
import { useTokenRefreshHandler } from "src/hooks";
import { useUserService } from "src/services";
import { WebinarGuestFilter, WebinarResponse } from "./useEventDetails";

/**
 * Hook that returns a function to fetch event guests imperatively.
 *
 * WHY THIS EXISTS (vs. useEventDetails):
 * --------------------------------------
 * The standard `useEventDetails` hook uses SWR for reactive data fetching,
 * which is great for displaying paginated data in the UI. However, when
 * opening the "Begin Follow Up" modal, we need to fetch ALL ready guests
 * at once (not just the current page) to display them in the modal.
 *
 * This hook returns an async function that can be called in event handlers
 * to fetch guests on-demand without pagination. It follows the same pattern
 * as mutation hooks (e.g., useBeginFollowUp) - setting up auth/headers at
 * hook level and returning an async function for imperative use.
 *
 * WHEN TO USE:
 * - When you need to fetch all guests matching a filter (no pagination)
 * - When you need to fetch data in response to a user action (e.g., button click)
 * - When SWR's reactive/cached behavior is not needed
 *
 * WHEN TO USE useEventDetails INSTEAD:
 * - When displaying paginated guest lists in the UI
 * - When you want automatic revalidation and caching
 * - When the data should update reactively
 */
export function useFetchAllEventGuests() {
  const service = useUserService();
  const tokenRefreshHandler = useTokenRefreshHandler();
  const accessToken = getUserToken();
  const override = useActingAsOverrideHeader();

  const headers: { [index: string]: string } = {
    "JWT-TOKEN": accessToken as string,
  };

  if (override) {
    headers.override = override;
  }

  return async (
    eventId: string | number,
    filter: Partial<WebinarGuestFilter>,
  ): Promise<WebinarResponse> => {
    // Don't include paging - we want ALL matching guests
    const query: Partial<WebinarGuestFilter> = {
      ...filter,
      paging: undefined,
    };

    const response = await service
      .post(`/api/webinars/${eventId}`)
      .set(headers)
      .send(query)
      .then(tokenRefreshHandler)
      .then((res: Response) => res.body);

    return response as WebinarResponse;
  };
}

export default useFetchAllEventGuests;
